// Share metadata for the marketing landing pages (#3881) — the single source
// of truth for their <title>, description and social share image.
//
// Two consumers, which is why this module must stay pure (no Vue, no
// `import.meta.env`, no `@/` imports):
//   - router/index.ts spreads `landingRouteMeta(path)` into the route `meta`,
//     and utils/routeMetadata.ts applies it client-side on navigation.
//   - functions/for/[page].ts (Cloudflare Pages Function) imports it by
//     relative path and rewrites the static index.html at the edge, because
//     link-preview crawlers (Facebook, LinkedIn, Slack) do not run JS.
//
// Image paths are site-relative; the Function turns them into absolute URLs
// from the request origin. The OG JPEGs are generated by
// scripts/generate-og-landing.mjs.

export interface OgImage {
  path: string
  width: number
  height: number
  alt: string
}

export interface LandingPageMeta {
  title: string
  description: string
  /** One or more share images. With several, the edge picks one per request. */
  ogImages: readonly [OgImage, ...OgImage[]]
}

export const LANDING_PAGES = {
  '/for/radgivere': {
    title: 'Bookingsystem for rådgivere | Stemne',
    description:
      'La kunden bestille selv. Stemne samler booking, innebygd video og interne kundenotater for deg som jobber med rådgivning. Se gjeldende priser og kom i gang.',
    ogImages: [
      {
        path: '/og/stemne-og-radgivere-a.jpg',
        width: 1200,
        height: 630,
        alt: 'To personer i samtale i hver sin lenestol i et lyst rom. Tekst: Bookingsystem for rådgivere.',
      },
      {
        path: '/og/stemne-og-radgivere-b.jpg',
        width: 1200,
        height: 630,
        alt: 'En rådgiver og en kunde i samtale ved et rundt bord. Tekst: Bookingsystem for rådgivere.',
      },
    ],
  },
  '/for/pt': {
    title: 'Bookingsystem for PT-er | Stemne',
    description:
      'Bookingsystem for PT-er og trenere. La kundene booke selv, planlegg økter og samle notater, kundehistorikk og oppfølging. Innebygd video. Prøv gratis i 3 måneder.',
    ogImages: [
      {
        path: '/og/stemne-og-pt-a.jpg',
        width: 1200,
        height: 630,
        alt: 'En PT veileder en kunde gjennom en knebøy med kettlebell. Tekst: Bookingsystem for PT-er.',
      },
      {
        path: '/og/stemne-og-pt-b.jpg',
        width: 1200,
        height: 630,
        alt: 'En PT og en kunde snakker sammen på en trebenk i et lyst studio. Tekst: Bookingsystem for PT-er.',
      },
    ],
  },
  '/for/behandlere': {
    title: 'Bookingsystem for massører og behandlere | Stemne',
    description:
      'La kundene bestille selv. Stemne samler booking, påminnelser og krypterte notater for massører og behandlere. Prøv gratis i 3 måneder.',
    ogImages: [
      {
        path: '/og/stemne-og-behandlere-a.jpg',
        width: 1200,
        height: 630,
        alt: 'En massør masserer skuldrene til en kunde på en behandlingsbenk. Tekst: Bookingsystem for massører og behandlere.',
      },
      {
        path: '/og/stemne-og-behandlere-b.jpg',
        width: 1200,
        height: 630,
        alt: 'En behandler og en kunde snakker sammen på en benk i et lyst rom. Tekst: Bookingsystem for massører og behandlere.',
      },
    ],
  },
} as const satisfies Record<string, LandingPageMeta>

export type LandingPath = keyof typeof LANDING_PAGES

export function isLandingPath(path: string): path is LandingPath {
  return Object.hasOwn(LANDING_PAGES, path)
}

/** The route `meta` fields the SPA reads (utils/routeMetadata.ts). */
export function landingRouteMeta(path: LandingPath): { title: string; description: string } {
  const { title, description } = LANDING_PAGES[path]
  return { title, description }
}
